/**
 * Super Admin Dashboard
 *
 * Centro de control del producto JH LUMINA. Enfoca al SUPER_ADMIN en
 * los KPIs de negocio (gestion de IPS, licencias, MRR, salud del
 * sistema), no en metricas operativas clinicas (esas pertenecen al
 * dashboard de cada ADMIN de IPS).
 */

import { Link } from 'react-router-dom'
import {
  Building2,
  AlertTriangle,
  AlertCircle,
  Info,
  Loader2,
  CreditCard,
  TrendingUp,
  Calendar,
  Activity,
  Database,
  HardDrive,
  Server,
  ArrowRight,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import {
  useBusinessKpis,
  useInstitutionsSummary,
  useRecentActivity,
} from '@/application/admin-dashboard/useAdminDashboard'
import { APP_ROUTES } from '@/config/constants'
import {
  SUBSCRIPTION_STATUS_LABELS,
  SUBSCRIPTION_STATUS_COLORS,
  type SubscriptionStatus,
} from '@/domain/subscription/types'

const formatCop = (n: number) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(n)

const formatDate = (iso: string | null): string => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const SeverityIcon = ({ severity }: { severity: 'info' | 'warning' | 'critical' }) => {
  if (severity === 'critical') return <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
  if (severity === 'warning') return <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
  return <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
}

const HealthBadge = ({ status }: { status: 'ok' | 'degraded' | 'error' }) => {
  const cls =
    status === 'ok'
      ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
      : status === 'degraded'
        ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200'
        : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
  const label = status === 'ok' ? 'Operativo' : status === 'degraded' ? 'Degradado' : 'Error'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  )
}

export default function SuperAdminDashboard() {
  const kpisQuery = useBusinessKpis()
  const summaryQuery = useInstitutionsSummary()
  const activityQuery = useRecentActivity()

  if (kpisQuery.isLoading || summaryQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      </div>
    )
  }

  const kpis = kpisQuery.data
  const summary = summaryQuery.data?.institutions ?? []
  const activity = activityQuery.data?.logs ?? []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Panel de Control</h1>
          <p className="text-[var(--foreground-muted)]">
            Gestiona instituciones, licencias y la salud del producto.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={APP_ROUTES.SUPER_ADMIN.INSTITUTIONS}>
            <Button>
              <Building2 className="mr-2 h-4 w-4" />
              Gestionar Instituciones
            </Button>
          </Link>
        </div>
      </div>

      {/* Alerts */}
      {kpis && kpis.alerts.length > 0 && (
        <div className="space-y-2">
          {kpis.alerts.map((alert, idx) => (
            <div
              key={idx}
              className={`rounded-md border p-3 flex items-start gap-3 ${
                alert.severity === 'critical'
                  ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40'
                  : alert.severity === 'warning'
                    ? 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/40'
                    : 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40'
              }`}
            >
              <SeverityIcon severity={alert.severity} />
              <div className="flex-1 text-sm">
                <p className="font-semibold">{alert.title}</p>
                <p className="text-[var(--foreground-muted)]">{alert.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* KPIs principales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wide text-[var(--foreground-muted)]">
              IPS activas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <Building2 className="h-5 w-5 text-[var(--primary)]" />
              <span className="text-3xl font-bold">
                {kpis?.institutionsByStatus.totalActiveInstitutions ?? 0}
              </span>
            </div>
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              Operativas en el sistema
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wide text-[var(--foreground-muted)]">
              MRR
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              <span className="text-3xl font-bold">
                {formatCop(kpis?.revenue.mrrCop ?? 0)}
              </span>
            </div>
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              Recurrente mensual estimado
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wide text-[var(--foreground-muted)]">
              Pagos del mes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-3xl font-bold">
                {formatCop(kpis?.revenue.paymentsThisMonthCop ?? 0)}
              </span>
            </div>
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              {kpis?.revenue.paymentsThisMonthCount ?? 0} transaccion(es)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wide text-[var(--foreground-muted)]">
              Vencen pronto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <Calendar className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              <span className="text-3xl font-bold">
                {kpis?.upcomingExpirations.next7Days ?? 0}
              </span>
            </div>
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              Suscripciones en proximos 7 dias
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Estados de IPS */}
      <Card>
        <CardHeader>
          <CardTitle>Distribucion de IPS por estado de suscripcion</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-center">
            {kpis && (
              <>
                <StatusTile
                  label="Activas"
                  count={kpis.institutionsByStatus.active}
                  color="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200"
                />
                <StatusTile
                  label="En trial"
                  count={kpis.institutionsByStatus.trial}
                  color="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200"
                />
                <StatusTile
                  label="En cortesia"
                  count={kpis.institutionsByStatus.gracePeriod}
                  color="bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200"
                />
                <StatusTile
                  label="Suspendidas"
                  count={kpis.institutionsByStatus.suspended}
                  color="bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200"
                />
                <StatusTile
                  label="Sin plan"
                  count={kpis.institutionsByStatus.withoutSubscription}
                  color="bg-gray-100 dark:bg-gray-900/40 text-gray-800 dark:text-gray-200"
                />
                <StatusTile
                  label="Trials por vencer"
                  count={kpis.trialsExpiringSoon}
                  color="bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200"
                />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de IPS */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Instituciones</CardTitle>
            <Link to={APP_ROUTES.SUPER_ADMIN.INSTITUTIONS}>
              <Button variant="outline" size="sm">
                Ver todas
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {summary.length === 0 ? (
              <div className="text-center text-[var(--foreground-muted)] py-8">
                <Building2 className="mx-auto h-10 w-10 opacity-40" />
                <p className="mt-2 text-sm">No hay instituciones registradas.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {summary.slice(0, 8).map((inst) => (
                  <div
                    key={inst.id}
                    className="flex items-center justify-between rounded-md border border-[var(--border)] px-3 py-2 text-sm hover:bg-[var(--background-muted)]"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{inst.name}</p>
                      <p className="text-xs text-[var(--foreground-muted)]">
                        {inst.city ?? 'Sin ciudad'}
                        {inst.plan && ` · ${inst.plan.name}`}
                      </p>
                    </div>
                    <div className="text-right text-xs">
                      {inst.subscription ? (
                        <>
                          <SubscriptionBadge status={inst.subscription.status as SubscriptionStatus} />
                          {inst.subscription.expiresAt && (
                            <p className="text-[var(--foreground-muted)] mt-1">
                              Vence {formatDate(inst.subscription.expiresAt)}
                            </p>
                          )}
                          {inst.subscription.trialEndsAt &&
                            !inst.subscription.expiresAt && (
                              <p className="text-[var(--foreground-muted)] mt-1">
                                Trial hasta {formatDate(inst.subscription.trialEndsAt)}
                              </p>
                            )}
                        </>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-900/40 text-gray-800 dark:text-gray-200">
                          Sin plan
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sistema + Actividad */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Salud del sistema</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Base de datos
                </span>
                <HealthBadge status={kpis?.systemHealth.database ?? 'error'} />
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <HardDrive className="h-4 w-4" />
                  Almacenamiento
                </span>
                <HealthBadge status={kpis?.systemHealth.storage ?? 'error'} />
              </div>
              {kpis?.systemHealth.schedulers.map((s) => (
                <div key={s.name} className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Server className="h-4 w-4" />
                    Scheduler: {s.name}
                  </span>
                  <HealthBadge status={s.isRunning ? 'ok' : 'error'} />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Actividad reciente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activity.length === 0 ? (
                <p className="text-sm text-[var(--foreground-muted)]">Sin actividad.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {activity.slice(0, 8).map((log) => (
                    <li key={log.id} className="border-l-2 border-[var(--border)] pl-2">
                      <p className="text-xs">
                        <span className="font-medium">{log.action}</span>{' '}
                        <span className="text-[var(--foreground-muted)]">
                          {log.resourceType}
                        </span>
                      </p>
                      <p className="text-xs text-[var(--foreground-muted)]">
                        {log.userEmail ?? 'sistema'} ·{' '}
                        {new Date(log.createdAt).toLocaleString('es-CO')}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

function StatusTile({
  label,
  count,
  color,
}: {
  label: string
  count: number
  color: string
}) {
  return (
    <div className="rounded-md border border-[var(--border)] p-3">
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}
      >
        {label}
      </span>
      <p className="text-2xl font-bold mt-2">{count}</p>
    </div>
  )
}

function SubscriptionBadge({ status }: { status: SubscriptionStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${SUBSCRIPTION_STATUS_COLORS[status]}`}
    >
      {SUBSCRIPTION_STATUS_LABELS[status]}
    </span>
  )
}
